import React from 'react';

import { BasicButton } from '@adsk/alloy-react-button';
import { ChevronDownIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableComponent } from '@adsk/alloy-react-theme';

import { useTranslation } from '../../i18n';
import { GetToggleButtonProps, DefaultItem } from '../../types';

type ToggleButtonProps<TItem extends DefaultItem> =
  StylableComponent<HTMLButtonElement> & {
    getToggleButtonProps?: GetToggleButtonProps;
    isOpen: boolean;
    disabled?: boolean;
    highlightButtonOnFocus?: boolean;
    temp?: TItem;
  };

const ToggleButton = <TItem extends DefaultItem>({
  style,
  isOpen,
  getToggleButtonProps,
  highlightButtonOnFocus,
  as,
  ...props
}: ToggleButtonProps<TItem>) => {
  const toggleButtonProps = getToggleButtonProps?.(props) || props;

  const { t } = useTranslation();
  const { tokens } = useTheme();
  const iconTokens = tokens?.component.dropdown?.control?.icon;

  return (
    <BasicButton
      data-testid={'Control__toggle'}
      aria-label={t('TOGGLE_MENU')}
      as={as}
      style={{
        padding: 0,
        height: 32,
        cursor: props.disabled ? 'not-allowed' : undefined,
        ...(highlightButtonOnFocus &&
          !isOpen && {
            '&:focus-within': {
              svg: {
                boxShadow: iconTokens?.focus?.['box-shadow'],
                borderRadius: iconTokens?.focus?.['border-radius'],
              },
            },
          }),
      }}
      {...toggleButtonProps}
    >
      <ChevronDownIcon
        style={[
          {
            color: iconTokens?._?.color,
            ...(isOpen && {
              transform: 'rotate(180deg)',
            }),
          },
          style,
        ]}
      />
    </BasicButton>
  );
};

export default ToggleButton;
